import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Wrench, Plus, Trash2, Save, Printer, Car, Bike, Settings, 
  FileText, Check, Building, Phone, MapPin, User, Hash,
  ChevronDown, Edit, Eye, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language configurations
const LANGUAGES = {
  en: { name: 'English', country: 'UK', vatRate: 20, currency: '£', vatLabel: 'VAT' },
  it: { name: 'Italiano', country: 'Italia', vatRate: 22, currency: '€', vatLabel: 'IVA' },
  ro: { name: 'Română', country: 'România', vatRate: 19, currency: 'Lei', vatLabel: 'TVA' }
};

const LABELS = {
  en: {
    title: 'SERVICE SHEET',
    serviceProvider: 'Service Provider',
    client: 'Client',
    vehicle: 'Vehicle Information',
    vin: 'VIN Number',
    registration: 'Registration',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    mileage: 'Mileage',
    services: 'Services Performed',
    description: 'Description',
    qty: 'Qty',
    price: 'Price',
    amount: 'Amount',
    subtotal: 'Subtotal',
    vat: 'VAT',
    total: 'Total',
    date: 'Service Date',
    car: 'Car',
    moto: 'Motorcycle',
    serviced: 'SERVICED',
    check: 'CHECK',
    notes: 'Notes',
    verifyAuth: 'Verify authenticity',
    scanToVerify: 'Scan to verify document',
    signature: 'Signature & Stamp'
  },
  it: {
    title: 'SCHEDA SERVIZIO',
    serviceProvider: 'Centro Assistenza',
    client: 'Cliente',
    vehicle: 'Informazioni Veicolo',
    vin: 'Numero Telaio',
    registration: 'Targa',
    make: 'Marca',
    model: 'Modello',
    year: 'Anno',
    mileage: 'Chilometraggio',
    services: 'Servizi Eseguiti',
    description: 'Descrizione',
    qty: 'Qtà',
    price: 'Prezzo',
    amount: 'Importo',
    subtotal: 'Subtotale',
    vat: 'IVA',
    total: 'Totale',
    date: 'Data Servizio',
    car: 'Auto',
    moto: 'Moto',
    serviced: 'REVISIONATO',
    check: 'CONTROLLATO',
    notes: 'Note',
    verifyAuth: 'Verifica autenticità',
    scanToVerify: 'Scansiona per verificare',
    signature: 'Firma e Timbro'
  },
  ro: {
    title: 'FIȘĂ SERVICE',
    serviceProvider: 'Service Auto',
    client: 'Client',
    vehicle: 'Informații Vehicul',
    vin: 'Serie Șasiu (VIN)',
    registration: 'Nr. Înmatriculare',
    make: 'Marcă',
    model: 'Model',
    year: 'An Fabricație',
    mileage: 'Kilometraj',
    services: 'Lucrări Efectuate',
    description: 'Descriere',
    qty: 'Cant.',
    price: 'Preț',
    amount: 'Valoare',
    subtotal: 'Subtotal',
    vat: 'TVA',
    total: 'Total',
    date: 'Data Service',
    car: 'Autoturism',
    moto: 'Motocicletă',
    serviced: 'VERIFICAT',
    check: 'CONTROL',
    notes: 'Observații',
    verifyAuth: 'Verifică autenticitatea',
    scanToVerify: 'Scanează pentru verificare',
    signature: 'Semnătură și ștampilă'
  }
};

export const AdminServicePage = () => {
  const { getAuthHeaders } = useApp();
  const [sheets, setSheets] = useState([]);
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [previewSheet, setPreviewSheet] = useState(null);
  const printRef = useRef(null);
  
  const [form, setForm] = useState({
    language: 'en',
    vehicle_type: 'car',
    vin_number: '',
    registration_number: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    mileage: '',
    client_name: '',
    client_phone: '',
    client_address: '',
    items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, with_vat: false }],
    subtotal: 0,
    vat_amount: 0,
    total: 0,
    include_vat: false,
    hide_prices: false,
    show_stamp: true,
    stamp_type: 'serviced',
    service_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [providerForm, setProviderForm] = useState({
    language: 'en',
    company_name: '',
    phone: '',
    main_address: '',
    sub_unit_address: '',
    vat_number: '',
    registration_number: ''
  });

  // Clients state
  const [clients, setClients] = useState([]);
  const [showClients, setShowClients] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sheetsRes, providersRes, clientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/service/sheets`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/service/providers`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/service/clients`, { headers: getAuthHeaders() })
      ]);
      setSheets(sheetsRes.data);
      setClients(clientsRes.data || []);
      
      // Convert providers array to object by language
      const providersMap = {};
      providersRes.data.forEach(p => {
        providersMap[p.language] = p;
      });
      setProviders(providersMap);
    } catch (error) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Client CRUD functions
  const handleSaveClient = async () => {
    if (!clientForm.name.trim()) {
      toast.error('Numele clientului este obligatoriu');
      return;
    }
    try {
      if (editingClient) {
        await axios.put(`${BACKEND_URL}/api/service/clients/${editingClient.id}`, clientForm, { headers: getAuthHeaders() });
        toast.success('Client actualizat');
      } else {
        await axios.post(`${BACKEND_URL}/api/service/clients`, clientForm, { headers: getAuthHeaders() });
        toast.success('Client salvat');
      }
      setClientForm({ name: '', phone: '', address: '' });
      setEditingClient(null);
      fetchData();
    } catch (error) {
      toast.error('Eroare la salvarea clientului');
    }
  };

  const handleEditClient = (client) => {
    setClientForm({
      name: client.name || '',
      phone: client.phone || '',
      address: client.address || ''
    });
    setEditingClient(client);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Ștergi acest client?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/service/clients/${clientId}`, { headers: getAuthHeaders() });
      toast.success('Client șters');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergerea clientului');
    }
  };

  const selectClient = (client) => {
    setForm({
      ...form,
      client_name: client.name || '',
      client_phone: client.phone || '',
      client_address: client.address || ''
    });
    toast.success(`Client selectat: ${client.name}`);
  };

  const handleAddItem = () => {
    setForm({
      ...form,
      items: [...form.items, { description: '', quantity: 1, unit_price: 0, amount: 0, with_vat: false }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems });
    calculateTotals(newItems, form.include_vat, form.language);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    
    // Calculate item amount
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setForm({ ...form, items: newItems });
    calculateTotals(newItems, form.include_vat, form.language);
  };

  const calculateTotals = (items, includeVat, language) => {
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const vatRate = LANGUAGES[language].vatRate;
    const vat_amount = includeVat ? subtotal * (vatRate / 100) : 0;
    const total = subtotal + vat_amount;
    
    setForm(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      vat_amount: Math.round(vat_amount * 100) / 100,
      total: Math.round(total * 100) / 100
    }));
  };

  const handleSaveProvider = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/service/providers/${providerForm.language}`,
        providerForm,
        { headers: getAuthHeaders() }
      );
      toast.success(`Provider ${LANGUAGES[providerForm.language].name} saved!`);
      setProviders({ ...providers, [providerForm.language]: providerForm });
    } catch (error) {
      toast.error('Failed to save provider');
    }
  };

  const loadProviderForEdit = (lang) => {
    const provider = providers[lang] || { language: lang };
    setProviderForm({
      language: lang,
      company_name: provider.company_name || '',
      phone: provider.phone || '',
      main_address: provider.main_address || '',
      sub_unit_address: provider.sub_unit_address || '',
      vat_number: provider.vat_number || '',
      registration_number: provider.registration_number || ''
    });
  };

  const handleSubmit = async () => {
    // Get provider data for selected language
    const provider = providers[form.language] || {};
    
    const sheetData = {
      ...form,
      provider_name: provider.company_name || '',
      provider_phone: provider.phone || '',
      provider_main_address: provider.main_address || '',
      provider_sub_unit: provider.sub_unit_address || '',
      vat_rate: LANGUAGES[form.language].vatRate
    };

    try {
      if (editingSheet) {
        await axios.put(
          `${BACKEND_URL}/api/service/sheets/${editingSheet.id}`,
          sheetData,
          { headers: getAuthHeaders() }
        );
        toast.success('Service sheet updated!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/service/sheets`,
          sheetData,
          { headers: getAuthHeaders() }
        );
        toast.success('Service sheet created!');
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save service sheet');
    }
  };

  const resetForm = () => {
    setForm({
      language: 'en',
      vehicle_type: 'car',
      vin_number: '',
      registration_number: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_year: '',
      mileage: '',
      client_name: '',
      client_phone: '',
      client_address: '',
      items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, with_vat: false }],
      subtotal: 0,
      vat_amount: 0,
      total: 0,
      include_vat: false,
      hide_prices: false,
      show_stamp: true,
      stamp_type: 'serviced',
      service_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowForm(false);
    setEditingSheet(null);
  };

  const handleEdit = (sheet) => {
    setForm({
      ...sheet,
      hide_prices: sheet.hide_prices || false,
      show_stamp: sheet.show_stamp !== false,
      items: sheet.items?.length > 0 ? sheet.items : [{ description: '', quantity: 1, unit_price: 0, amount: 0, with_vat: false }]
    });
    setEditingSheet(sheet);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ștergi această fișă service?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/service/sheets/${id}`, { headers: getAuthHeaders() });
      toast.success('Service sheet deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePrint = (sheet) => {
    setPreviewSheet(sheet);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const formatCurrency = (amount, lang) => {
    const currency = LANGUAGES[lang]?.currency || '£';
    return `${currency}${amount.toFixed(2)}`;
  };

  // Print Preview Component
  const PrintPreview = ({ sheet }) => {
    const labels = LABELS[sheet.language] || LABELS.en;
    const config = LANGUAGES[sheet.language] || LANGUAGES.en;
    const hidePrices = sheet.hide_prices || false;
    
    return (
      <div className="print-area fixed inset-0 bg-white z-[9999] overflow-auto" style={{ display: previewSheet ? 'block' : 'none' }}>
        <style>{`
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            html, body {
              height: auto !important;
              overflow: visible !important;
              background: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body * {
              visibility: hidden !important;
            }
            .print-area, .print-area * {
              visibility: visible !important;
            }
            .print-area {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              background: white !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .print-content {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              max-width: 100% !important;
              margin: 0 auto !important;
              padding: 5mm !important;
            }
            .no-print {
              display: none !important;
            }
            /* Hide app header, footer, sidebar */
            header, footer, nav, .glass, [class*="AdminLayout"] {
              display: none !important;
            }
          }
          @media screen {
            .print-area {
              padding: 20px;
            }
          }
        `}</style>
        
        <div className="print-content max-w-3xl mx-auto bg-white text-black p-6">
          {/* Header */}
          <div className="flex justify-between items-start border-b-2 border-gray-800 pb-3 mb-4">
            <div>
              <h1 className="text-xl font-bold">{labels.title}</h1>
              <p className="text-xs text-gray-600">#{sheet.sheet_number}</p>
              <p className="text-xs">{labels.date}: {sheet.service_date}</p>
            </div>
            <div className="text-right">
              <div className={`inline-block px-4 py-1 border-3 rounded ${
                sheet.stamp_type === 'serviced' ? 'border-green-600 text-green-600' : 'border-blue-600 text-blue-600'
              } font-bold text-base transform rotate-[-5deg]`} style={{ borderWidth: '3px' }}>
                {sheet.stamp_type === 'serviced' ? labels.serviced : labels.check}
              </div>
            </div>
          </div>

          {/* Provider & Client */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border border-gray-300 p-3 rounded text-sm">
              <h3 className="font-bold text-xs uppercase text-gray-600 mb-1">{labels.serviceProvider}</h3>
              <p className="font-semibold text-sm">{sheet.provider_name}</p>
              <p className="text-xs">{sheet.provider_main_address}</p>
              {sheet.provider_sub_unit && <p className="text-xs">{sheet.provider_sub_unit}</p>}
              <p className="text-xs">{sheet.provider_phone}</p>
            </div>
            <div className="border border-gray-300 p-3 rounded text-sm">
              <h3 className="font-bold text-xs uppercase text-gray-600 mb-1">{labels.client}</h3>
              <p className="font-semibold text-sm">{sheet.client_name}</p>
              <p className="text-xs">{sheet.client_address}</p>
              <p className="text-xs">{sheet.client_phone}</p>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="border border-gray-300 p-3 rounded mb-4">
            <h3 className="font-bold text-xs uppercase text-gray-600 mb-2 flex items-center gap-1">
              {sheet.vehicle_type === 'car' ? '🚗' : '🏍️'}
              {labels.vehicle} ({sheet.vehicle_type === 'car' ? labels.car : labels.moto})
            </h3>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-gray-600">{labels.vin}:</span> <strong>{sheet.vin_number}</strong></div>
              <div><span className="text-gray-600">{labels.registration}:</span> <strong>{sheet.registration_number}</strong></div>
              <div><span className="text-gray-600">{labels.mileage}:</span> <strong>{sheet.mileage}</strong></div>
              <div><span className="text-gray-600">{labels.make}:</span> {sheet.vehicle_make}</div>
              <div><span className="text-gray-600">{labels.model}:</span> {sheet.vehicle_model}</div>
              <div><span className="text-gray-600">{labels.year}:</span> {sheet.vehicle_year}</div>
            </div>
          </div>

          {/* Services Table */}
          <div className="mb-4">
            <h3 className="font-bold text-xs uppercase text-gray-600 mb-2">{labels.services}</h3>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-1 text-left text-xs">{labels.description}</th>
                  <th className="border border-gray-300 p-1 text-center w-12 text-xs">{labels.qty}</th>
                  {!hidePrices && (
                    <>
                      <th className="border border-gray-300 p-1 text-right w-20 text-xs">{labels.price}</th>
                      <th className="border border-gray-300 p-1 text-right w-20 text-xs">{labels.amount}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sheet.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 p-1 text-xs">{item.description}</td>
                    <td className="border border-gray-300 p-1 text-center text-xs">{item.quantity}</td>
                    {!hidePrices && (
                      <>
                        <td className="border border-gray-300 p-1 text-right text-xs">{formatCurrency(item.unit_price, sheet.language)}</td>
                        <td className="border border-gray-300 p-1 text-right text-xs">{formatCurrency(item.amount, sheet.language)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom section: Company Stamp (left) + Totals (right) */}
          <div className="flex justify-between items-end mb-3">
            {/* Company Stamp - Small Rectangle (conditional) */}
            {(sheet.show_stamp !== false) && (
              <div className="border border-gray-800 p-2 w-44">
                <div className="text-center">
                  <p className="font-bold text-[10px] uppercase leading-tight">{sheet.provider_name}</p>
                  <p className="text-[9px] text-gray-600 leading-tight">{sheet.provider_main_address}</p>
                  {sheet.provider_sub_unit && <p className="text-[9px] text-gray-600 leading-tight">{sheet.provider_sub_unit}</p>}
                  <p className="text-[9px] text-gray-600">{sheet.provider_phone}</p>
                  <div className="border-t border-gray-300 mt-1 pt-1">
                    <p className="text-[8px] text-gray-500 italic">{labels.signature}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Spacer when no stamp */}
            {(sheet.show_stamp === false) && <div></div>}

            {/* Totals - only show if prices are not hidden */}
            {!hidePrices && (
              <div className="w-40 text-sm">
                <div className="flex justify-between py-0.5 border-b">
                  <span className="text-[10px]">{labels.subtotal}:</span>
                  <strong className="text-[10px]">{formatCurrency(sheet.subtotal, sheet.language)}</strong>
                </div>
                {sheet.include_vat && (
                  <div className="flex justify-between py-0.5 border-b">
                    <span className="text-[10px]">{labels.vat} ({sheet.vat_rate}%):</span>
                    <strong className="text-[10px]">{formatCurrency(sheet.vat_amount, sheet.language)}</strong>
                  </div>
                )}
                <div className="flex justify-between py-0.5 font-bold">
                  <span className="text-xs">{labels.total}:</span>
                  <span className="text-xs">{formatCurrency(sheet.total, sheet.language)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {sheet.notes && (
            <div className="border-t pt-1 mb-2">
              <h3 className="font-bold text-[10px] uppercase text-gray-600 mb-0.5">{labels.notes}</h3>
              <p className="text-[10px]">{sheet.notes}</p>
            </div>
          )}

          {/* Footer with QR Code */}
          <div className="border-t pt-2 flex items-center gap-3">
            <QRCodeSVG 
              value={`${window.location.origin}/v/${sheet.verify_code || sheet.id}`}
              size={45}
              level="M"
            />
            <div className="text-[9px] text-gray-600">
              <p className="font-medium">{labels.scanToVerify}</p>
              <p className="text-[8px] text-gray-500">{labels.verifyAuth}</p>
            </div>
          </div>

          {/* Close button (no-print) */}
          <div className="no-print mt-6 text-center flex justify-center gap-4">
            <Button onClick={() => setPreviewSheet(null)} variant="outline">
              Închide Preview
            </Button>
            <Button onClick={() => window.print()} className="bg-primary">
              <Printer size={16} className="mr-2" />
              Printează
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif flex items-center gap-3">
              <Wrench className="text-primary" />
              Service Auto/Moto
            </h1>
            <p className="text-muted-foreground mt-1">Fișe service pentru UK, Italia și România</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettings(!showSettings)}>
              <Settings size={18} className="mr-2" />
              Setări Service
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }}>
              <Plus size={18} className="mr-2" />
              Fișă Nouă
            </Button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="glass p-6">
            <h2 className="text-xl font-semibold mb-4">Setări Service per Țară</h2>
            
            {/* Language tabs */}
            <div className="flex gap-2 mb-4">
              {Object.entries(LANGUAGES).map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => loadProviderForEdit(code)}
                  className={`px-4 py-2 rounded ${
                    providerForm.language === code 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                >
                  {lang.name} ({lang.country})
                </button>
              ))}
            </div>

            {/* Provider form */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">Nume Firmă</label>
                <input
                  type="text"
                  value={providerForm.company_name}
                  onChange={(e) => setProviderForm({ ...providerForm, company_name: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                  placeholder="Auto Service SRL"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">Telefon</label>
                <input
                  type="text"
                  value={providerForm.phone}
                  onChange={(e) => setProviderForm({ ...providerForm, phone: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                  placeholder="+40 xxx xxx xxx"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">Adresă Principală</label>
                <input
                  type="text"
                  value={providerForm.main_address}
                  onChange={(e) => setProviderForm({ ...providerForm, main_address: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                  placeholder="Str. Exemplu nr. 1, Oraș"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">Subunitate / Punct de Lucru</label>
                <input
                  type="text"
                  value={providerForm.sub_unit_address}
                  onChange={(e) => setProviderForm({ ...providerForm, sub_unit_address: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                  placeholder="(opțional)"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">CUI / VAT Number</label>
                <input
                  type="text"
                  value={providerForm.vat_number}
                  onChange={(e) => setProviderForm({ ...providerForm, vat_number: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-1 block">Nr. Reg. Comerțului</label>
                <input
                  type="text"
                  value={providerForm.registration_number}
                  onChange={(e) => setProviderForm({ ...providerForm, registration_number: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-4">
              <Button onClick={handleSaveProvider} className="bg-primary">
                <Save size={16} className="mr-2" />
                Salvează {LANGUAGES[providerForm.language].name}
              </Button>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {editingSheet ? 'Editează Fișă Service' : 'Fișă Service Nouă'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-slate-700 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Language & Type Selection */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Limbă / Țară</label>
                  <select
                    value={form.language}
                    onChange={(e) => {
                      setForm({ ...form, language: e.target.value });
                      calculateTotals(form.items, form.include_vat, e.target.value);
                    }}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  >
                    {Object.entries(LANGUAGES).map(([code, lang]) => (
                      <option key={code} value={code}>{lang.name} ({lang.country})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Tip Vehicul</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, vehicle_type: 'car' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded ${
                        form.vehicle_type === 'car' ? 'bg-primary text-primary-foreground' : 'bg-slate-800'
                      }`}
                    >
                      <Car size={18} /> Auto
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, vehicle_type: 'moto' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded ${
                        form.vehicle_type === 'moto' ? 'bg-primary text-primary-foreground' : 'bg-slate-800'
                      }`}
                    >
                      <Bike size={18} /> Moto
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Ștampilă</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, stamp_type: 'serviced' })}
                      className={`flex-1 px-4 py-2 rounded border-2 ${
                        form.stamp_type === 'serviced' 
                          ? 'border-green-500 text-green-500' 
                          : 'border-slate-600 text-slate-400'
                      }`}
                    >
                      ✓ SERVICED
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, stamp_type: 'check' })}
                      className={`flex-1 px-4 py-2 rounded border-2 ${
                        form.stamp_type === 'check' 
                          ? 'border-blue-500 text-blue-500' 
                          : 'border-slate-600 text-slate-400'
                      }`}
                    >
                      ✓ CHECK
                    </button>
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div>
                <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <Car size={16} />
                  Informații Vehicul
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Serie Șasiu (VIN)</label>
                    <input
                      type="text"
                      value={form.vin_number}
                      onChange={(e) => setForm({ ...form, vin_number: e.target.value.toUpperCase() })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm font-mono"
                      placeholder="WVWZZZ3CZWE123456"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Nr. Înmatriculare</label>
                    <input
                      type="text"
                      value={form.registration_number}
                      onChange={(e) => setForm({ ...form, registration_number: e.target.value.toUpperCase() })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm font-mono"
                      placeholder={form.language === 'ro' ? 'IS 01 ABC' : form.language === 'it' ? 'AA 123 BB' : 'AB12 CDE'}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Kilometraj</label>
                    <input
                      type="text"
                      value={form.mileage}
                      onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      placeholder="125000 km"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Marcă</label>
                    <input
                      type="text"
                      value={form.vehicle_make}
                      onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      placeholder="Volkswagen"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Model</label>
                    <input
                      type="text"
                      value={form.vehicle_model}
                      onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      placeholder="Golf"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">An Fabricație</label>
                    <input
                      type="text"
                      value={form.vehicle_year}
                      onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      placeholder="2020"
                    />
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                    <User size={16} />
                    Informații Client
                  </h3>
                  <Button 
                    onClick={() => setShowClients(!showClients)} 
                    size="sm" 
                    variant="outline"
                    className="text-xs"
                  >
                    {showClients ? 'Ascunde Clienți' : 'Clienți Salvați'} ({clients.length})
                  </Button>
                </div>
                
                {/* Saved Clients List */}
                {showClients && (
                  <div className="mb-4 p-3 bg-slate-700/30 rounded-lg border border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={clientForm.name}
                        onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                        className="flex-1 bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm"
                        placeholder="Nume client nou..."
                      />
                      <input
                        type="text"
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                        className="w-32 bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm"
                        placeholder="Telefon"
                      />
                      <input
                        type="text"
                        value={clientForm.address}
                        onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                        className="w-40 bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm"
                        placeholder="Adresă"
                      />
                      <Button onClick={handleSaveClient} size="sm" className="bg-green-600 hover:bg-green-700">
                        <Save size={14} />
                      </Button>
                      {editingClient && (
                        <Button 
                          onClick={() => { setEditingClient(null); setClientForm({ name: '', phone: '', address: '' }); }} 
                          size="sm" 
                          variant="outline"
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    {clients.length > 0 ? (
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {clients.map((client) => (
                          <div 
                            key={client.id} 
                            className="flex items-center justify-between p-2 bg-slate-800/50 rounded hover:bg-slate-700/50 cursor-pointer group"
                            onClick={() => selectClient(client)}
                          >
                            <div className="flex-1">
                              <span className="font-medium text-sm">{client.name}</span>
                              {client.phone && <span className="text-xs text-muted-foreground ml-2">{client.phone}</span>}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleEditClient(client); }}
                                className="p-1 hover:bg-blue-500/20 rounded"
                              >
                                <Edit size={12} className="text-blue-400" />
                              </button>
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}
                                className="p-1 hover:bg-red-500/20 rounded"
                              >
                                <Trash2 size={12} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">Niciun client salvat</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Nume Client</label>
                    <input
                      type="text"
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      placeholder="Ion Popescu"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Telefon</label>
                    <input
                      type="text"
                      value={form.client_phone}
                      onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Adresă</label>
                    <input
                      type="text"
                      value={form.client_address}
                      onChange={(e) => setForm({ ...form, client_address: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Service Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                    <Wrench size={16} />
                    Lucrări Efectuate
                  </h3>
                  <Button onClick={handleAddItem} size="sm" variant="outline">
                    <Plus size={14} className="mr-1" /> Adaugă Rând
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {form.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Descriere lucrare..."
                        className="col-span-5 bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="col-span-1 bg-slate-800 border border-white/10 rounded px-2 py-2 text-sm text-center"
                        min="1"
                      />
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        placeholder="Preț"
                        className="col-span-2 bg-slate-800 border border-white/10 rounded px-2 py-2 text-sm"
                        step="0.01"
                      />
                      <div className="col-span-2 text-right font-medium">
                        {formatCurrency(item.amount, form.language)}
                      </div>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="col-span-1 p-2 text-red-400 hover:bg-red-400/10 rounded"
                        disabled={form.items.length === 1}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* VAT & Totals */}
              <div className="flex justify-between items-end">
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.include_vat}
                      onChange={(e) => {
                        setForm({ ...form, include_vat: e.target.checked });
                        calculateTotals(form.items, e.target.checked, form.language);
                      }}
                      className="w-5 h-5 rounded"
                    />
                    <span>Include {LANGUAGES[form.language].vatLabel} ({LANGUAGES[form.language].vatRate}%)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.hide_prices}
                      onChange={(e) => setForm({ ...form, hide_prices: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-orange-400">Ascunde prețuri</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.show_stamp}
                      onChange={(e) => setForm({ ...form, show_stamp: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-blue-400">Ștampilă firmă</span>
                  </label>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Data Service</label>
                    <input
                      type="date"
                      value={form.service_date}
                      onChange={(e) => setForm({ ...form, service_date: e.target.value })}
                      className="bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                
                <div className="text-right space-y-1">
                  <div className="flex justify-between gap-8">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(form.subtotal, form.language)}</span>
                  </div>
                  {form.include_vat && (
                    <div className="flex justify-between gap-8">
                      <span className="text-muted-foreground">{LANGUAGES[form.language].vatLabel} ({LANGUAGES[form.language].vatRate}%):</span>
                      <span className="font-medium">{formatCurrency(form.vat_amount, form.language)}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-8 text-lg font-bold border-t pt-2">
                    <span>Total:</span>
                    <span className="text-primary">{formatCurrency(form.total, form.language)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Observații</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                  placeholder="Note suplimentare..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={resetForm}>
                  Anulează
                </Button>
                <Button onClick={handleSubmit} className="bg-primary">
                  <Save size={16} className="mr-2" />
                  {editingSheet ? 'Actualizează' : 'Generează Fișă'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Sheets List */}
        <div className="glass overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="font-semibold">Fișe Service ({sheets.length})</h2>
          </div>
          
          {sheets.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Wrench size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nu există fișe service</p>
              <p className="text-sm">Creează prima fișă folosind butonul "Fișă Nouă"</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Nr. Fișă</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Țară</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Vehicul</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Client</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Total</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Status</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map((sheet) => (
                  <tr key={sheet.id} className="border-b border-white/5 hover:bg-slate-800/50">
                    <td className="p-4 font-mono">{sheet.sheet_number}</td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-1 bg-slate-700 rounded">
                        {LANGUAGES[sheet.language]?.country || 'UK'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {sheet.vehicle_type === 'car' ? <Car size={16} /> : <Bike size={16} />}
                        <span>{sheet.vehicle_make} {sheet.vehicle_model}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{sheet.registration_number}</div>
                    </td>
                    <td className="p-4">{sheet.client_name}</td>
                    <td className="p-4 font-semibold">{formatCurrency(sheet.total, sheet.language)}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded border-2 font-bold ${
                        sheet.stamp_type === 'serviced' 
                          ? 'border-green-500 text-green-500' 
                          : 'border-blue-500 text-blue-500'
                      }`}>
                        {sheet.stamp_type === 'serviced' ? 'SERVICED' : 'CHECK'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <button
                          onClick={() => setPreviewSheet(sheet)}
                          className="p-2 hover:bg-blue-500/20 text-blue-400 rounded"
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handlePrint(sheet)}
                          className="p-2 hover:bg-slate-700 rounded"
                          title="Print"
                        >
                          <Printer size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(sheet)}
                          className="p-2 hover:bg-green-500/20 text-green-400 rounded"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(sheet.id)}
                          className="p-2 hover:bg-red-500/20 text-red-400 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Print Preview */}
        {previewSheet && <PrintPreview sheet={previewSheet} />}
      </div>
    </AdminLayout>
  );
};

export default AdminServicePage;
